import React, {forwardRef, useMemo, useReducer, useState, useEffect} from 'react';
import {FormGroup, InputGroup, Intent} from '@blueprintjs/core';
import {useSelector} from 'react-redux';

import {IState} from '../../../store/reducers';
import {IItem} from '../../select';
import {ContentCard, ContentToolbar, MessageResult} from '../components';
import {
  useCreateBlock, useExecuteRequest, useInitBlock, useLoadResult,
  useUpdateBlockContentJson,
} from '../hooks';
import {BaseBlock, ContentActions, ContentOptions} from '../types';
import {defaultContentActions, defaultContentOptions} from '../constants';
import {contentReducer, setContentFormKeysValues, setContentFormKeyValue} from '../store';
import {initialContentState} from './store';
import MethodInput from './MethodInput';
import SelectOption from './SelectOption';
import {createDefaultContent, destinationOptions, sourceOptions} from './utils';
import {blockResultSelector} from '../../../store/reducers/blockResultReducer';
import {shouldLoadResult} from '../utils';

import './Writeback.scss';

interface WritebackProps {
  content: BaseBlock;
  options?: ContentOptions;
  actions?: ContentActions;
}

export const Writeback = forwardRef(({
  content,
  options = defaultContentOptions,
  actions = defaultContentActions,
}: WritebackProps, ref: any) => {
  options = useMemo((): ContentOptions => ({...defaultContentOptions, ...options}), [options?.readOnly]);
  actions = useMemo((): ContentActions => ({...defaultContentActions, ...actions}), []);

  const [contentState, contentDispatch] = useReducer(contentReducer, initialContentState);
  const [destinationBackend, setDestinationBackend] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<{ [key: string]: string | null }>({});

  const uid = content.uid;
  const result = useSelector(blockResultSelector(uid as string, content.result_id as string));
  const resultId: string | null = content.result_id || result?.key || null;

  const blocks = useSelector((state: IState) => state.block.blocks); // source
  const databases = useSelector((state: IState) => state.databases.databases); // destination
  const _sourceOptions: IItem[] = sourceOptions(blocks);
  const _destinationOptions: IItem[] = destinationOptions(databases);

  /*
   * These default values are only used on Writeback create, so we only need a value in case we don't have uid.
   */
  const defaultSourceOption: IItem | undefined = useMemo(
    () => uid ? undefined : (_sourceOptions?.[0] || undefined), []);
  const defaultDestinationOption: IItem | undefined = useMemo(
    () => uid ? undefined : _destinationOptions?.[0] || undefined, []);

  const {executing, executeRequest} = useExecuteRequest(uid);
  const {updating, updateBlockContentJson} = useUpdateBlockContentJson(uid, options.readOnly, options.autosave);

  useCreateBlock(content, createDefaultContent(defaultSourceOption, defaultDestinationOption));
  useInitBlock(uid, content, contentDispatch);
  useLoadResult(uid, resultId,
    options.loadResult !== undefined ? options.loadResult : shouldLoadResult(content.result_id, result)
  );

  useEffect(() => {
    const selectedDestination = _destinationOptions.find(option => option.value === contentState.form.destination);
    if (selectedDestination) {
      setDestinationBackend(selectedDestination.backend);
    }
  }, [contentState.form.destination, _destinationOptions]);

  const onExecute = () => {
    let isValid = true;
    const errors: { [key: string]: string | null } = {};

    if (!contentState.form.schema) {
      errors.schema = "Schema is required.";
      isValid = false;
    }

    if (formErrors.schema) {
      isValid = false;
    }

    setFormErrors(errors);

    if (!isValid) {
      return;
    }

    const block: any = {
      content_json: {
        source: contentState.form.source || defaultSourceOption?.label,
        source_uid: contentState.form.source_uid || defaultSourceOption?.value,
        method: contentState.form.method,
        destination: contentState.form.destination,
        schema: contentState.form.schema,
      }
    };
    executeRequest(block);
  };

  const onSourceChange = (value: any) => {
    const newContent: any = {
      source: value.label,
      source_uid: value.value,
    };

    const keys = Object.keys(newContent);
    const values: any = Object.values(newContent);

    contentDispatch(setContentFormKeysValues(keys, values));
    updateBlockContentJson(keys, values);
  };

  const onDestinationChange = (value: any) => {
    const destination = value.value;
    const backend = value.backend;

    setDestinationBackend(backend);

    contentDispatch(setContentFormKeyValue('destination', destination));
    updateBlockContentJson('destination', destination);
  };

  const onSchemaChange = (event: any) => {
    const value = event.target.value;
    const schemaTablePattern = /^[a-zA-Z_]+\.[a-zA-Z_]+$/;
    const urlPattern = /^https:\/\/docs\.google\.com\/spreadsheets\/.*$/;
    const errors = { ...formErrors };

    if (destinationBackend === 'gsheets') {
      if (!urlPattern.test(value) && !schemaTablePattern.test(value)) {
        errors.schema = "For Google Sheets, the 'Schema + Table' field must be either a valid Google Sheets link or follow the format: 'spreadsheet_name.sheet_name' (e.g., public.sw).";
      } else {
        errors.schema = null;
      }
    } else {
      if (!schemaTablePattern.test(value)) {
        errors.schema = "The 'Schema + Table' field must follow the format: 'schema.table' (e.g., public.my_table).";
      } else {
        errors.schema = null;
      }
    }

    setFormErrors(errors);
    contentDispatch(setContentFormKeyValue('schema', value));
    updateBlockContentJson('schema', value);
  };

  const onMethodChange = (value: string) => {
    contentDispatch(setContentFormKeyValue('method', value));
    updateBlockContentJson('method', value);
  };

  return (
    <ContentCard
      key={content.id}
      type={content.type}
      contentId={uid}
      disabled={options.readOnly}
      noContentElement={undefined}
      allowCollapse={options.allowCollapse}
      showHeader={options.showHeader}
    >
      <div className="content__writeback">
        <ContentToolbar
          show={options?.showToolbar}
          contentId={uid as string}
          updating={updating}
          executing={executing}
          onExecute={onExecute}
          showSelect={false}
          onDelete={actions.onDelete}
          showDelete={options.showDelete}
          showSave={false}
          showPreview={false}
          disabled={options.readOnly || false}
        />
        <div className="content-card__content__form">
          <div className="content__writeback__form">
            <SelectOption
              label="Source"
              selectLabel="Data source not found. Please add an SQL, API JSON or a Tableau CSV block to continue."
              selected={contentState.form.source_uid || defaultSourceOption?.value}
              options={_sourceOptions}
              onChange={onSourceChange}
              disabled={options.readOnly || false}
            />
            <SelectOption
              label="Destination"
              selectLabel="No destination databases found. Please create a new database first."
              selected={contentState.form.destination || defaultDestinationOption?.value}
              options={_destinationOptions}
              onChange={onDestinationChange}
              disabled={options.readOnly || false}
            />
            <FormGroup
              label="Schema + Table"
              inline={true}
              intent={formErrors.schema ? Intent.DANGER : Intent.NONE}
            >
              <InputGroup
                value={contentState.form.schema}
                onChange={onSchemaChange}
                disabled={options.readOnly}
                placeholder="public.my_new_table"
                fill={true}
                intent={formErrors.schema ? Intent.DANGER : Intent.NONE}
              />
              {formErrors.schema && (
                <div className="schema-error">
                  {formErrors.schema}
                </div>
              )}
            </FormGroup>
            <MethodInput
              value={contentState.form.method}
              onChange={onMethodChange}
              disabled={options.readOnly || false}
            />
          </div>
          <MessageResult
            result={result}
          />
        </div>
      </div>
    </ContentCard>
  );
});
